import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Instagram, Twitter, Linkedin, Facebook, ArrowRight } from "lucide-react";

export default function Footer({ api }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await axios.post(`${api}/newsletter`, { email });
      toast.success("Subscribed.", {
        description: "One short letter a month — never spam.",
      });
      setEmail("");
    } catch (err) {
      toast.error("Couldn't subscribe. Try again?");
    } finally {
      setBusy(false);
    }
  };

  return (
    <footer
      data-testid="site-footer"
      className="relative bg-ink text-cream pt-20 md:pt-28 pb-10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 pb-16 border-b border-cream/15">
          <div className="md:col-span-5">
            <h3 className="font-serif text-4xl md:text-5xl leading-tight text-cream text-balance">
              One short letter, every month — about the city we're building together.
            </h3>
            <form
              onSubmit={subscribe}
              data-testid="newsletter-form"
              className="mt-8 flex flex-col sm:flex-row gap-3 max-w-md"
            >
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                data-testid="newsletter-input"
                className="flex-1 bg-transparent border border-cream/30 text-cream px-4 py-3 placeholder:text-cream/40 focus:outline-none focus:border-mustard"
              />
              <button
                type="submit"
                disabled={busy}
                data-testid="newsletter-submit"
                className="inline-flex items-center justify-center gap-2 bg-mustard hover:bg-terracotta text-ink hover:text-white px-6 py-3 transition-colors disabled:opacity-60"
              >
                Subscribe
                <ArrowRight size={16} />
              </button>
            </form>
          </div>

          <div className="md:col-span-2 md:col-start-8">
            <p className="overline text-mustard mb-4">Explore</p>
            <ul className="space-y-3 text-sm">
              <FooterLink href="#about" testid="footer-link-about">About</FooterLink>
              <FooterLink href="#programs" testid="footer-link-programs">Programs</FooterLink>
              <FooterLink href="#impact" testid="footer-link-impact">Impact</FooterLink>
              <FooterLink href="#events" testid="footer-link-events">Our Work</FooterLink>
            </ul>
          </div>
          <div className="md:col-span-2">
            <p className="overline text-mustard mb-4">Act</p>
            <ul className="space-y-3 text-sm">
              <FooterLink href="#volunteer" testid="footer-link-volunteer">Volunteer</FooterLink>
              <FooterLink href="#donate" testid="footer-link-donate">Donate</FooterLink>
              <FooterLink href="#contact" testid="footer-link-contact">Contact</FooterLink>
            </ul>
          </div>
          <div className="md:col-span-3 md:col-start-12 hidden md:block" />
        </div>

        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 pt-10">
          <div className="flex items-center gap-3">
            <img
              src="/logos/simple-3-sapling-dot.jpg"
              alt="JRSDWA logo"
              data-testid="footer-logo-img"
              className="w-16 h-16 object-contain bg-cream rounded-md"
            />
            <div className="flex flex-col leading-none">
              <span className="font-serif text-lg md:text-xl text-cream">
                Jharkhand Rastriya Sarva Dharma Welfare Association
              </span>
              <span className="overline text-[10px] text-cream/60 mt-2">
                Registered 2007 — Reg. No. JRSDWA/2007/JH-0042
              </span>
            </div>
          </div>

          <div className="flex items-center gap-4">
            {[
              { Icon: Instagram, label: "Instagram" },
              { Icon: Twitter, label: "Twitter" },
              { Icon: Linkedin, label: "LinkedIn" },
              { Icon: Facebook, label: "Facebook" },
            ].map(({ Icon, label }) => (
              <a
                key={label}
                href="#"
                aria-label={label}
                data-testid={`footer-social-${label.toLowerCase()}`}
                className="w-10 h-10 border border-cream/25 flex items-center justify-center hover:border-mustard hover:text-mustard transition-colors"
              >
                <Icon size={16} strokeWidth={1.5} />
              </a>
            ))}
          </div>

          <p className="text-xs text-cream/50">
            © {new Date().getFullYear()} Jharkhand Rastriya Sarva Dharma Welfare Association. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}

function FooterLink({ href, children, testid }) {
  return (
    <li>
      <a
        href={href}
        data-testid={testid}
        className="text-cream/75 hover:text-mustard transition-colors"
      >
        {children}
      </a>
    </li>
  );
}
